/* ============================================================
   calista-custom.js - Calista global behavior script (Layer 2)
   Single source of truth for ALL JS behaviors.
   Loaded with `defer` via XO Insert Code (footer/body-end)
   or settings.additional_head.
   NEVER edit native Eurus JS - attach to native elements here.

   Pattern: one self-contained init per behavior, each guarded
   so it no-ops when its target isn't on the page. All behaviors
   below are safe to run site-wide.

   Behaviors:
   - Mobile gallery pagination dots      (ported: layout/theme.liquid)
   - Yotpo review-count separator        (ported: review-product.liquid)
   - PDP video-shopping per-video caption
   - PDP: move Shop Pay installments below the CTA
   - PDP: default breadcrumb collection on direct/homepage entry
   - Card "Shop Now" -> PDP on mobile (not quick-add)
   ============================================================ */

(function () {
  'use strict';

  /* -- Mobile Gallery Pagination Dots --
     Builds the .x-calista-dots / .x-calista-dot DOM that
     calista-custom.css styles. Mobile only. Syncs the active
     dot to the active Splide slide via MutationObserver. */
  function initCalistaDots() {
    if (window.innerWidth >= 768) return;

    var mediaContainer = document.querySelector('.product-media-container');
    if (!mediaContainer) return;

    var track = mediaContainer.querySelector('.swiper-product');
    if (!track) return;

    var slides = track.querySelectorAll('.splide__slide');
    if (slides.length <= 1) return;

    if (mediaContainer.querySelector('.x-calista-dots')) return;

    var dots = document.createElement('div');
    dots.className = 'x-calista-dots';

    slides.forEach(function (_, i) {
      var dot = document.createElement('button');
      dot.className = 'x-calista-dot' + (i === 0 ? ' is-active' : '');
      dot.setAttribute('aria-label', 'Go to slide ' + (i + 1));
      dots.appendChild(dot);
    });

    mediaContainer.appendChild(dots);

    new MutationObserver(function () {
      var active = track.querySelector('.splide__slide.is-active');
      if (!active) return;
      var index = parseInt(active.getAttribute('index') || '0', 10);
      mediaContainer.querySelectorAll('.x-calista-dot').forEach(function (d, i) {
        d.classList.toggle('is-active', i === index);
      });
    }).observe(track, { attributes: true, subtree: true, attributeFilter: ['class'] });
  }
  /* -- END Mobile Gallery Pagination Dots -- */

  /* -- Yotpo Review-Count Thousands Separator --
     Yotpo renders the review count client-side (1069 -> 1,069),
     so no CSS can do this. Pierces shadow DOM and re-applies on
     re-render. Self-guarded against double-init. */
  function initReviewCountSeparator() {
    if (window.__calistaReviewFmt) return;
    window.__calistaReviewFmt = true;

    function collectRoots() {
      var roots = [document];
      var all = document.querySelectorAll('*');
      for (var i = 0; i < all.length; i++) {
        if (all[i].shadowRoot) roots.push(all[i].shadowRoot);
      }
      return roots;
    }
    function fmt() {
      var roots = collectRoots();
      for (var r = 0; r < roots.length; r++) {
        var els = roots[r].querySelectorAll('.yotpo-sr-bottom-line-text, .text-m, .yotpo-bottomline .yotpo-sr-bottom-line-text');
        for (var j = 0; j < els.length; j++) {
          var el = els[j];
          var t = el.textContent;
          var m = t.match(/\d[\d,]*/);
          if (!m) continue;
          var n = parseInt(m[0].replace(/,/g, ''), 10);
          if (isNaN(n)) continue;
          var f = n.toLocaleString('en-US');
          if (f !== m[0]) { el.textContent = t.replace(/\d[\d,]*/, f); }
        }
      }
    }
    var pending = false;
    function schedule() {
      if (pending) return;
      pending = true;
      requestAnimationFrame(function () { pending = false; fmt(); });
    }
    if (document.readyState !== 'loading') { fmt(); }
    else { document.addEventListener('DOMContentLoaded', fmt); }
    new MutationObserver(schedule).observe(document.documentElement, {
      childList: true, subtree: true, characterData: true
    });
  }
  /* -- END Yotpo Review-Count Thousands Separator -- */

  /* -- PDP Video Shopping: per-video captions --
     Native video-shopping has no caption field, so surface each video's
     "Video alt text" setting as a visible label below the video. Enter
     the caption (e.g. "How-To Curl") in the block's alt-text field - it
     doubles as the accessibility alt. Idempotent + observes the slider. */
  function injectVideoCaptions(root) {
    var cards = (root || document).querySelectorAll('[class*="card-video-shopping-"]');
    cards.forEach(function (card) {
      if (card.querySelector('.x-video-caption')) return;
      var videoWrap = card.querySelector('[id^="video--"]');
      var img = (videoWrap || card).querySelector('img[alt]');
      var alt = img ? (img.getAttribute('alt') || '').trim() : '';
      if (!alt) return;
      var cap = document.createElement('div');
      cap.className = 'x-video-caption';
      cap.textContent = alt;
      if (videoWrap) {
        videoWrap.insertAdjacentElement('afterend', cap);
      } else {
        card.appendChild(cap);
      }
    });
  }

  function initVideoCaptions() {
    injectVideoCaptions(document);
    var container = document.querySelector('.x-container-video-shopping');
    if (!container) return;
    var pending = false;
    new MutationObserver(function () {
      if (pending) return;
      pending = true;
      requestAnimationFrame(function () {
        pending = false;
        injectVideoCaptions(document);
      });
    }).observe(container, { childList: true, subtree: true });
  }
  /* -- END PDP Video Shopping: per-video captions -- */

  /* -- PDP: move Shop Pay installments below the CTA --
     Native theme renders <shopify-payment-terms> as the tail of the price
     block (ungated, not reorderable). Promote it to just after the buy
     buttons. On variant change Eurus may re-render a fresh copy back in the
     price block, so always promote the newest one and clear any stale copy
     we placed earlier - keeps the amount current and never duplicated. */
  function movePaymentTerms() {
    document.querySelectorAll('.x-block-buy-buttons').forEach(function (buyBlock) {
      var col = buyBlock.closest('.product-content-container') || buyBlock.parentElement;
      if (!col) return;
      var terms = col.querySelectorAll('.shopify-payment-terms');
      if (!terms.length) return;
      // "source" = the copy NOT already sitting right after the CTA
      var source = null;
      terms.forEach(function (t) { if (t.previousElementSibling !== buyBlock) source = t; });
      if (!source) return; // already in place, nothing new to promote
      // drop any stale copy we moved on a prior pass
      terms.forEach(function (t) { if (t !== source && t.previousElementSibling === buyBlock) t.remove(); });
      buyBlock.insertAdjacentElement('afterend', source);
    });
  }

  function initPaymentTerms() {
    movePaymentTerms();
    var scope = document.querySelector('.product-content-container');
    if (!scope) return;
    var pending = false;
    new MutationObserver(function () {
      if (pending) return;
      pending = true;
      requestAnimationFrame(function () { pending = false; movePaymentTerms(); });
    }).observe(scope, { childList: true, subtree: true });
  }
  /* -- END PDP: move Shop Pay installments -- */

  /* -- PDP: default breadcrumb collection on direct/homepage entry --
     Native breadcrumb only shows a collection crumb when the visitor
     arrived through one. When it's absent, insert the fallback (data comes
     from the Custom Liquid block .cal-bc-default) right after "All products"
     so it reads "All products > Category". Placing it in that exact slot
     lets the existing breadcrumb-shorten CSS govern both the native and the
     injected case with no special rule. No-ops if the data block is absent
     or a native collection crumb already exists. */
  function injectBreadcrumbCollection() {
    var data = document.querySelector('.cal-bc-default');
    if (!data || !data.dataset.url) return;
    document.querySelectorAll('.x-block-breadcrumb').forEach(function (nav) {
      var links = nav.querySelectorAll('a.nav-link');
      if (links.length >= 3) return;                 // native collection crumb present
      if (nav.querySelector('.cal-bc-injected')) return;
      var allProducts = links[links.length - 1];     // "All products" (2nd link)
      if (!allProducts) return;

      var src = nav.querySelector('.breadcrumb__divider');
      var divider = src
        ? src.cloneNode(true)
        : Object.assign(document.createElement('span'), { className: 'breadcrumb__divider', textContent: '/' });

      var link = document.createElement('a');
      link.className = 'nav-link cal-bc-injected text-[rgba(var(--colors-text-link))] relative h-fit before-link';
      link.href = data.dataset.url;
      link.textContent = data.dataset.title;

      // insert after "All products" -> All products, divider, link, ...
      allProducts.insertAdjacentElement('afterend', link);
      allProducts.insertAdjacentElement('afterend', divider);
    });
  }
  /* -- END PDP: default breadcrumb collection -- */

  /* -- Card "Shop Now": navigate to the PDP on mobile (not quick-add) --
     The card CTA is Eurus's NATIVE quick-add button
     (.button--quickview.button-solid, rendered by card-product-list-layout
     .liquid and card-product.liquid). Natively it adds to cart (single-variant
     / swatch) or opens choose-options (multi-variant) via an Alpine
     @click.prevent handler on the button. Eurus has no setting to make it link
     to the product page instead, so intercept it here.

     BREAKPOINT: the "bottom" Shop Now button (list-layout line ~1315,
     card-product line ~1268) is the instance shown BELOW Eurus's `lg`
     breakpoint (< 1024px); the desktop on-image hover button (`hidden lg:block`)
     is a SEPARATE element only visible at >= 1024px. So guarding at < 1024px
     intercepts exactly the bottom button across phone AND tablet, and never
     touches the desktop hover button (which can't be clicked below 1024px).
     (v1 guarded <= 767px, so at 768-1023px the bottom button still added to
     cart while this sat disabled - that was the bug.)

     A single delegated, CAPTURE-phase listener catches the tap before Alpine's
     bubble-phase @click, stops the event (stopImmediatePropagation prevents
     Alpine's addToCart; preventDefault cancels the type=submit form submission),
     reads the product URL from the card's own product <a href>, and navigates
     there. Delegation on document covers Alpine's client-rendered buttons on
     every card surface (collection grid + homepage Featured Collection) with no
     per-card wiring. A belt-and-suspenders capture listener on 'submit' catches
     any path that reaches form submission directly.

     Excludes .label-btn-quickview (the quick-VIEW magnifier launcher, already
     hidden via CSS). To include desktop too, raise the 1024 threshold. */
  function initCardShopNowToPdp() {
    if (window.__calistaCardShopNowBound) return;   // bind once
    window.__calistaCardShopNowBound = true;

    // Deploy marker: on the live site, type  window.__calistaJS  in the console.
    // If it returns this string the new file is live; if it's undefined the old
    // cached asset is still being served (re-upload / hard refresh / temp ?v=).
    window.__calistaJS = 'card-pdp-v2';

    function cardPdpUrl(target) {
      var btn = target.closest && target.closest('button.button--quickview.button-solid');
      if (!btn || btn.classList.contains('label-btn-quickview')) return null;
      if (!window.matchMedia('(max-width: 1023px)').matches) return null;  // bottom button only
      var card = btn.closest('.card-product');
      if (!card) return null;
      var link = card.querySelector('a[href*="/products/"]');
      return link ? link.getAttribute('href') : null;
    }

    // Primary: intercept the click before Alpine's @click / the native submit.
    document.addEventListener('click', function (e) {
      var url = cardPdpUrl(e.target);
      if (!url) return;
      e.preventDefault();
      e.stopImmediatePropagation();
      window.location.href = url;
    }, true);

    // Insurance: if anything still reaches form submission, catch it too.
    document.addEventListener('submit', function (e) {
      if (!window.matchMedia('(max-width: 1023px)').matches) return;
      var form = e.target;
      if (!form.closest || !form.closest('.card-product')) return;
      var btn = form.querySelector('button.button--quickview.button-solid:not(.label-btn-quickview)');
      if (!btn) return;
      var card = form.closest('.card-product');
      var link = card && card.querySelector('a[href*="/products/"]');
      if (!link) return;
      e.preventDefault();
      e.stopImmediatePropagation();
      window.location.href = link.getAttribute('href');
    }, true);
  }
  /* -- END Card "Shop Now": navigate to the PDP on mobile -- */

  function init() {
    setTimeout(initCalistaDots, 300);
    initReviewCountSeparator();
    initVideoCaptions();
    initPaymentTerms();
    injectBreadcrumbCollection();
    initCardShopNowToPdp();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();

/* End: calista-custom.js */